import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function PortalLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) {
    if (user.senha_temporaria) return <Navigate to="/app/trocar-senha" replace />;
    return <Navigate to={user.role === "cliente" ? "/cliente/home" : "/app/dashboard"} replace />;
  }

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const data = await login(email.trim(), password);
      const u = data.user || data;
      if (u.role !== "cliente") {
        toast.error("Este e-mail não é de cliente. Use /login para acesso interno.");
        return;
      }
      toast.success(`Bem-vindo(a), ${u.name}`);
      if (u.senha_temporaria) {
        toast.warning("Você precisa definir uma nova senha agora.", { duration: 5000 });
        nav("/app/trocar-senha", { replace: true });
        return;
      }
      nav("/cliente/home", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no login");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/cliente/home";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md p-8 sm:p-10 shadow-xl border-0">
        <div className="flex flex-col items-center text-center mb-6">
          <Logo size="md" />
          <h1 className="mt-4 text-2xl font-display font-bold" style={{ color: "#1B3A6B" }}>
            Área do cliente
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie sua frota e seus chamados
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mail, nome ou CNPJ</Label>
            <Input
              id="email"
              data-testid="cliente-login-email"
              type="text"
              inputMode="text"
              placeholder="voce@empresa.com.br ou WMS SUPERMERCADOS..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="pwd">Senha</Label>
            <div className="relative mt-1.5">
              <Input
                id="pwd"
                data-testid="cliente-login-password"
                type={showPwd ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                data-testid="cliente-login-toggle-pwd"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="cliente-login-submit"
            className="w-full text-white font-semibold h-11"
            style={{ background: "#F97316" }}
          >
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
            Acessar minha conta
          </Button>
        </form>

        <div className="my-6 flex items-center gap-2 text-xs text-gray-400">
          <div className="flex-1 h-px bg-gray-200" />
          ou
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        <Button
          type="button"
          onClick={googleLogin}
          data-testid="cliente-login-google"
          variant="outline"
          className="w-full h-11 font-medium"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Entrar com Google
        </Button>

        <div className="mt-6 text-center text-xs text-gray-500 space-y-1">
          <button
            type="button"
            className="hover:text-orange-500 transition"
            onClick={() => toast.info("Fale com seu consultor para redefinir sua senha")}
          >
            Esqueci minha senha
          </button>
          <div>
            Não tenho cadastro?{" "}
            <span className="text-gray-700">Fale com seu consultor.</span>
          </div>
        </div>
      </Card>
    </div>
  );
}
